import type { QueryClient } from '@tanstack/react-query';
import { api, setDemoApiAdapter } from '@/lib/api';
import { useAuthStore } from '@/auth/authStore';
import {
  isLiveStudyExperienceRequested,
  setLiveStudyExperience,
  type LiveStudyExperience,
} from './mode';

/** One-use server ticket, exchanged once before mounting. Access stays in memory; restoration uses a separate HttpOnly experience cookie. */
export async function installLiveStudyExperience(client: QueryClient): Promise<boolean> {
  if (!isLiveStudyExperienceRequested()) return false;
  const ticket = new URLSearchParams(window.location.hash.slice(1)).get('ticket');
  const destination = window.location.hash.startsWith('#/learn/study')
    ? window.location.hash
    : '#/learn/study';
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${destination}`,
  );
  setDemoApiAdapter(null);
  const expectedSpace = new URLSearchParams(window.location.search).get('space');
  const session = await api<LiveStudyExperience & { access_token: string; experience_id: string }>(
    ticket ? '/study-experience/exchange' : '/study-experience/refresh',
    {
      method: 'POST',
      body: ticket ? { ticket } : expectedSpace ? { experience_id: expectedSpace } : {},
      skipAuthRefresh: true,
      headers: { 'X-Airbotix-Client': 'app' },
    },
  );
  const search = new URLSearchParams(window.location.search);
  search.set('space', session.experience_id);
  window.history.replaceState(null, '', `${window.location.pathname}?${search}${destination}`);
  setLiveStudyExperience(session);
  useAuthStore.getState().setToken('kid', session.access_token);
  useAuthStore.getState().setBootstrapped(true);
  client.setQueryData(['auth', 'me', 'kid'], session.learner);
  client.setQueryData(['auth', 'me', 'user'], session.learner);
  return true;
}
